import { TransferStatusBadge } from '@/components/badges/TransferStatusBadge'
import { SectionCard } from '@/components/SectionCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { getProductName } from '@/features/equipment/utils/product'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useUI } from '@/hooks/useUI'
import { CalendarDays, MapPin, Package, Plus, Truck, User } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetActiveTransferRequests } from '../hooks/useTransferRequest'
import { BorrowTransferDialog } from './BorrowTransferDialog'
import { HandleTransferDialog } from './HandleTransferDialog'
import { TransferOutDialog } from './TransferOutDialog'

export function RequestsView() {
  const { t } = useTranslation()
  const [borrowOpen, setBorrowOpen] = useState(false)
  const [transferOutOpen, setTransferOutOpen] = useState(false)
  const [selectedTrId, setSelectedTrId] = useState<string | null>(null)
  const [handleOpen, setHandleOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState('all')
  const { currentUser } = useAuth()
  const { activeTransferStatuses } = useUI()
  const { formatDateTime } = useDateFormatter()

  const statusParam = statusFilter === 'all' ? undefined : statusFilter
  const { data: transfers = [], isLoading } = useGetActiveTransferRequests(
    {
      status: statusParam,
    },
    !!currentUser,
  )

  const filtered = transfers.filter(r => {
    if (statusFilter === 'all') {
      if (activeTransferStatuses?.length === 0) return true
      return activeTransferStatuses?.some(status => status.value === r.status)
    }

    return r.status === statusFilter
  })

  const pendingCount = transfers.filter(r => r.status === 'pending').length
  const awaitingCount = transfers.filter(r => r.status === 'awaiting-receive').length

  return (
    <>
      <SectionCard
        data-testid="transfers-list"
        header={
          <div className="flex items-center gap-3">
            <CardTitle className="text-sm">
              {t('requests.title')} ({filtered.length})
            </CardTitle>
            {pendingCount > 0 && (
              <Badge className="bg-warning/15 text-warning text-[10px]">
                {pendingCount} {t('requests.pending')}
              </Badge>
            )}
            {awaitingCount > 0 && (
              <Badge className="bg-info/15 text-info text-[10px]">
                {awaitingCount} {t('requests.awaitingReceive')}
              </Badge>
            )}
          </div>
        }
        right={
          <>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="transfers-status-filter" className="h-8 w-[140px] bg-secondary/50 text-xs">
                <SelectValue placeholder={t('common.status')} />
              </SelectTrigger>
              <SelectContent className="bg-card text-foreground">
                <SelectItem value="all">{t('common.allStatuses')}</SelectItem>
                {activeTransferStatuses?.map(s => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button data-testid="transfers-transfer-out-button" size="sm" variant="outline" className="h-8 text-xs" onClick={() => setTransferOutOpen(true)}>
              <Truck className="mr-1 size-3" />
              {t('requests.transferOut')}
            </Button>
            <Button data-testid="transfers-borrow-in-button" size="sm" className="h-8 text-xs" onClick={() => setBorrowOpen(true)}>
              <Plus className="mr-1 size-3" />
              {t('requests.borrowIn')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {isLoading ? (
            <div className="flex flex-col items-center py-12 text-center">
              <Truck className="size-10 text-muted-foreground/30" />
              <p className="mt-3 text-xs text-muted-foreground">{t('common.loading')}</p>
            </div>
          ) : (
            filtered.map(tr => {
              return (
                <div
                  key={tr.id}
                  data-testid="transfer-card"
                  className="flex cursor-pointer flex-col gap-2 rounded-lg border border-border/30 bg-secondary/20 p-4 transition-colors hover:border-primary/30"
                  onClick={() => {
                    setSelectedTrId(tr.id)
                    setHandleOpen(true)
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-foreground">
                          {getProductName(tr.productName, tr.productBrand, tr.productModel) || '-'}
                        </span>
                        <TransferStatusBadge status={tr.status} />
                      </div>
                      <span className="text-xs text-muted-foreground">{tr.reason}</span>
                    </div>
                    {tr.completedAt && (
                      <span className="text-[10px] text-muted-foreground">
                        {formatDateTime(tr.completedAt, {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-[10px] text-muted-foreground">
                    <span>{t('requests.requester')}:</span>
                    <span className="flex items-center gap-1">
                      <User className="size-2.5" />
                      <span>{tr.requesterName || '-'}</span>
                    </span>
                    <span>{t('requests.from')}:</span>
                    <span className="flex items-center gap-1">
                      <Package className="size-2.5" />
                      <span>{tr.sourceDepartmentName || '-'}</span>
                    </span>
                    <span>{t('requests.to')}:</span>
                    <span className="flex items-center gap-1">
                      <MapPin className="size-2.5" />
                      <span>{tr.targetDepartmentName || '-'}</span>
                    </span>
                    {tr.scheduledDate && (
                      <div className="flex items-center gap-1">
                        <span>{t('common.date')}:</span>
                        <span className="flex items-center gap-1">
                          <CalendarDays className="size-2.5" />
                          <span>
                            {formatDateTime(tr.scheduledDate, {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )
            })
          )}
          {!isLoading && filtered.length === 0 && (
            <div className="flex flex-col items-center py-12 text-center">
              <Truck className="size-10 text-muted-foreground/30" />
              <p className="mt-3 text-xs text-muted-foreground">{t('requests.noRequests')}</p>
            </div>
          )}
        </div>
      </SectionCard>

      <BorrowTransferDialog open={borrowOpen} onOpenChange={setBorrowOpen} />
      <TransferOutDialog open={transferOutOpen} onOpenChange={setTransferOutOpen} />
      <HandleTransferDialog transferId={selectedTrId} open={handleOpen} onOpenChange={setHandleOpen} />
    </>
  )
}
